import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  IconButton, 
  Input, 
  SearchInput,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Alert,
  Typography,
  Heading2,
  Body,
  Small,
  Caption,
  Label,
  cn
} from '../design-system/index';
import { CalendarLayout } from '../design-system/components/Layout/CalendarLayout';
import { CalendarContainer, CalendarHeader } from '../design-system/components/Layout/CalendarLayout';
import { useAuth } from '../context/AuthContext';
import { useNotes } from '../context/NoteContext';
import taskService from '../services/taskService';
import noteService from '../services/noteService';
import { useToast } from '../hooks/use-toast';
import SketchEditor from '../components/SketchEditor';
import noteServiceInstance from '../services/noteService';
import './notes-screen.css';

// Simple Modal Component using design system components
const SimpleModal = ({ isOpen, onClose, title, children, className }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <Card 
        className={cn("modal-card", className)}
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="modal-header">
            <CardTitle>{title}</CardTitle>
            <IconButton
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
            >
              <i className="fa-solid fa-times" />
            </IconButton>
          </div>
        </CardHeader>
        <CardContent>
          {children}
        </CardContent>
      </Card>
    </div>
  );
};

const NotesScreen = () => {
  const { user, isAuthenticated } = useAuth();
  const { notes, filteredNotes, categories, fetchNotes, deleteNote, loading, filters, updateFilters, connectNoteToTask, disconnectNoteFromTask, searchTasks } = useNotes();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State management
  const [darkMode, setDarkMode] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: '#a0d2eb' });
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isSketchModalOpen, setIsSketchModalOpen] = useState(false);
  const [currentSketchId, setCurrentSketchId] = useState(null);
  const editorRef = useRef(null);

  // Navigation items matching the calendar pattern
  const navigationItems = [
    { id: 'analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line' },
    { id: 'meetings', label: 'Meetings', icon: 'fa-solid fa-users' },
    { id: 'notes', label: 'Notes', icon: 'fa-solid fa-sticky-note' },
    { id: 'tasks', label: 'Tasks', icon: 'fa-solid fa-list-check' },
    { id: 'categories', label: 'Categories', icon: 'fa-solid fa-layer-group' },
  ];

  // Pastel color palette for category creation
  const pastelColors = useMemo(() => [
    { name: 'Pastel Blue', value: '#a0d2eb' },
    { name: 'Pastel Purple', value: '#d0bdf4' },
    { name: 'Pastel Pink', value: '#f4c2c2' },
    { name: 'Pastel Green', value: '#b5ead7' },
    { name: 'Pastel Yellow', value: '#fef9e7' },
    { name: 'Pastel Orange', value: '#fdcb6e' },
    { name: 'Pastel Mint', value: '#c7f9cc' },
    { name: 'Pastel Lavender', value: '#d4c1ec' },
    { name: 'Pastel Peach', value: '#fad0c3' },
    { name: 'Pastel Teal', value: '#98ddca' }
  ], []);

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedDarkMode === 'true' || (savedDarkMode === null && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Handle navigation
  const handleNavigation = (item) => {
    switch (item.id) {
      case 'analytics':
        navigate('/dashboard');
        break;
      case 'meetings':
        navigate('/calendar');
        break;
      case 'notes':
        navigate('/notes');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'categories':
        navigate('/tasks');
        break;
      default:
        navigate('/dashboard');
    }
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  // Note management functions
  const handleNoteClick = (noteId) => {
    navigate(`/n/${noteId}`);
  };

  const handleDeleteNote = async (e, noteId) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await deleteNote(noteId);
        toast({
          title: 'Note deleted successfully',
          variant: "default",
        });
      } catch (error) {
        toast({
          title: 'Failed to delete note',
          variant: "destructive",
        });
        console.error(error);
      }
    }
  };

  const handleEditNote = (e, noteId) => {
    e.stopPropagation();
    navigate(`/notes/${noteId}/edit`);
  };

  const handleShareNote = (e, noteId) => {
    e.stopPropagation();
    const url = `${window.location.origin}/n/${noteId}`;
    navigator.clipboard.writeText(url.replace("app.v2.organisewise.me", "orgwi.se").replace("app.organisewise.me", "orgwi.se"))
      .then(() => {
        toast({
          title: "Note link copied to clipboard!",
          variant: "default",
        });
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        toast({
          title: "Failed to copy link to clipboard",
          variant: "destructive",
        });
      });
  };

  const handleTogglePublic = async (e, noteId, isPublic) => {
    e.stopPropagation();
    
    try {
      await noteService.togglePublic(noteId, isPublic);
      await fetchNotes();
      toast({
        title: `Note is now ${isPublic ? 'private' : 'public'}`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: 'Failed to update note visibility',
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const handleLinkToTask = (e, note) => {
    e.stopPropagation();
    
    if (note.taskId) {
      if (window.confirm('Do you want to disconnect this note from its task?')) {
        disconnectNoteFromTask(note.id, note.taskId)
          .then(() => {
            toast({
              title: 'Note disconnected from task',
              variant: "default",
            });
          })
          .catch((error) => {
            toast({
              title: 'Failed to disconnect note from task',
              variant: "destructive",
            });
            console.error(error);
          });
      }
    } else {
      setSelectedNote(note);
      setIsTaskModalOpen(true);
      setTaskSearchQuery('');
      setSearchResults([]);
      setSelectedTask(null);
    }
  };

  // Task linking functions
  const handleTaskSearch = async () => {
    if (!taskSearchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await searchTasks(taskSearchQuery);
      setSearchResults(results);
    } catch (error) {
      toast({
        title: 'Failed to search tasks',
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTaskSelect = (task) => {
    setSelectedTask(selectedTask && selectedTask.id === task.id ? null : task);
  };

  const handleConnectTask = async () => {
    if (!selectedTask || !selectedNote) return;
    
    try {
      await connectNoteToTask(selectedNote.id, selectedTask.id);
      toast({
        title: 'Note connected to task successfully',
        variant: "default",
      });
      setIsTaskModalOpen(false);
    } catch (error) {
      toast({
        title: 'Failed to connect note to task',
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedNote(null);
    setTaskSearchQuery('');
    setSearchResults([]);
    setSelectedTask(null);
  };

  // Category management
  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setNewCategory({ name: '', color: '#a0d2eb' });
  };

  const handleColorSelect = (color) => {
    setNewCategory(prev => ({ ...prev, color }));
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast({
        title: 'Category name is required',
        variant: 'destructive',
      });
      return;
    }
    
    setIsCreatingCategory(true);
    try {
      await taskService.createCategory({
        name: newCategory.name,
        color: newCategory.color
      });
      
      toast({
        title: 'Category created successfully',
        variant: 'default',
      });
      
      handleCloseCategoryModal();
      await fetchNotes();
      
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: 'Failed to create category',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingCategory(false);
    }
  };

  // Note creation functions
  const handleAddNote = async () => {
    const noteResponse = await noteServiceInstance.createNote({
      title: `New Note - ${new Date().toLocaleString()}`,
      content: "Date: " + new Date().toLocaleDateString(),
      tags: []
    });
    const noteId = noteResponse.id;
    navigate('/notes/' + noteId);
  };

  const handleAddSketch = () => {
    setIsSketchModalOpen(true);
  };

  const handleSketchSave = async (sketchId) => {
    setIsSketchModalOpen(false);
    if (sketchId) {
      setCurrentSketchId(sketchId);
      const noteResponse = await noteServiceInstance.createNote({
        title: `Sketch Note - ${new Date().toLocaleString()}`,
        content: "This is a new note created from a sketch \n ```excalidraw\n ref= " + sketchId + "\n```",
        tags: ['sketch']
      });
      const noteId = noteResponse.id;
      navigate('/notes/' + noteId);
    }
  };

  // Utility functions
  const formatDate = (dateObject) => {
    if (!dateObject) return '';
    return dateObject.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: dateObject.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const truncateContent = (content, maxLength = 150) => {
    if (!content) return '';
    const plainText = content.replace(/<[^>]*>/g, '');
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  const handleTaskClick = (e, taskId) => {
    e.stopPropagation();
    if (taskId) {
      navigate(`/tasks/${taskId}`);
    }
  };

  // Stats calculations
  const calculateStats = () => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    // Total notes
    const totalNotes = notes.length;

    // This week's notes
    const thisWeekNotes = notes.filter(note => {
      const noteDate = note.createdAt || note.updatedAt;
      return noteDate && noteDate >= oneWeekAgo;
    }).length;

    // Last week's notes
    const lastWeekNotes = notes.filter(note => {
      const noteDate = note.createdAt || note.updatedAt;
      return noteDate && noteDate >= twoWeeksAgo && noteDate < oneWeekAgo;
    }).length;

    // Top category
    const categoryCounts = {};
    notes.forEach(note => {
      if (note.categoryDetails) {
        const catName = note.categoryDetails.name;
        categoryCounts[catName] = (categoryCounts[catName] || 0) + 1;
      }
    });

    const topCategory = Object.entries(categoryCounts).length > 0
      ? Object.entries(categoryCounts).reduce((a, b) => a[1] > b[1] ? a : b)
      : ['No Category', 0];

    return {
      totalNotes,
      thisWeekNotes,
      lastWeekNotes,
      topCategory: { name: topCategory[0], count: topCategory[1] }
    };
  };

  const stats = calculateStats();

  // Render functions
  const renderStatsCards = () => (
    <div className="notes-stats">
      <Card 
        className="stats-card" 
        variant={darkMode ? "dark-accent" : "default"}
        hover="lift"
      >
        <CardContent className="stats-card-content">
          <div className="stats-icon">
            <i className="fa-solid fa-sticky-note" style={{ color: darkMode ? '#8b5cf6' : '#3b82f6' }} />
          </div>
          <div className="stats-info">
            <div className="stats-label">Total Notes</div>
            <div className="stats-value">{stats.totalNotes}</div>
          </div>
        </CardContent>
      </Card>

      <Card 
        className="stats-card" 
        variant={darkMode ? "dark-accent" : "default"}
        hover="lift"
      >
        <CardContent className="stats-card-content">
          <div className="stats-icon">
            <i className="fa-solid fa-calendar-week" style={{ color: darkMode ? '#22d3ee' : '#10b981' }} />
          </div>
          <div className="stats-info">
            <div className="stats-label">This Week</div>
            <div className="stats-value">{stats.thisWeekNotes}</div>
          </div>
        </CardContent>
      </Card>

      <Card 
        className="stats-card" 
        variant={darkMode ? "dark-accent" : "default"}
        hover="lift"
      >
        <CardContent className="stats-card-content">
          <div className="stats-icon">
            <i className="fa-solid fa-calendar-minus" style={{ color: darkMode ? '#fbbf24' : '#f59e0b' }} />
          </div>
          <div className="stats-info">
            <div className="stats-label">Last Week</div>
            <div className="stats-value">{stats.lastWeekNotes}</div>
          </div>
        </CardContent>
      </Card>

      <Card 
        className="stats-card" 
        variant={darkMode ? "dark-accent" : "default"}
        hover="lift"
      >
        <CardContent className="stats-card-content">
          <div className="stats-icon">
            <i className="fa-solid fa-star" style={{ color: darkMode ? '#fb7185' : '#ef4444' }} />
          </div>
          <div className="stats-info">
            <div className="stats-label">Top Category</div>
            <div className="stats-value">{stats.topCategory.count}</div>
            <div className="stats-sublabel">{stats.topCategory.name}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderFilterButtons = () => (
    <div className="notes-filters">
      <Button 
        variant={filters.category === 'all' ? (darkMode ? "accent" : "primary") : "ghost"}
        size="sm"
        onClick={() => updateFilters({ category: 'all' })}
      >
        All {notes.length}
      </Button>
      {categories.map(cat => {
        const count = notes.filter(note => note.categoryId === cat.id).length;
        if (count === 0) return null;
        return (
          <Button 
            key={cat.id}
            variant={filters.category === cat.id ? (darkMode ? "accent" : "primary") : "ghost"}
            size="sm"
            onClick={() => updateFilters({ category: cat.id })}
            className="notes-category-filter"
            style={{
              '--category-color': cat.color,
              borderLeft: filters.category === cat.id ? `3px solid ${cat.color}` : 'none'
            }}
          >
            <span 
              className="category-dot"
              style={{ backgroundColor: cat.color }}
            />
            {cat.name} {count}
          </Button>
        );
      })}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleOpenCategoryModal}
        leftIcon={<i className="fa-solid fa-plus" />}
        className="notes-add-category"
      >
        Add Category
      </Button>
    </div>
  );

  const renderNoteCard = (note) => {
    const categoryColor = note.categoryDetails ? note.categoryDetails.color : (darkMode ? '#7c3aed' : '#2563eb');
    
    return (
      <div 
        key={note.id}
        className="note-card"
        onClick={() => handleNoteClick(note.id)}
        style={{ '--category-color': categoryColor }}
      >
        {/* Header */}
        <div className="note-card-header">
          <h3 className="note-title">{note.title || 'Untitled Note'}</h3>
          <div className="note-actions">
            <IconButton
              size="icon-sm"
              variant="ghost"
              onClick={(e) => handleShareNote(e, note.id)}
              title="Share"
            >
              <i className="fa-solid fa-share-alt" />
            </IconButton>
            <IconButton
              size="icon-sm"
              variant="ghost"
              onClick={(e) => handleLinkToTask(e, note)}
              title="Link to Task"
            >
              <i className="fa-solid fa-link" />
            </IconButton>
            <IconButton
              size="icon-sm"
              variant="ghost"
              onClick={(e) => handleTogglePublic(e, note.id, note.isShared)}
              title={note.isShared ? "Make Private" : "Make Public"}
            >
              <i className={`fa-solid ${note.isShared ? 'fa-unlock' : 'fa-lock'}`} />
            </IconButton>
            <IconButton
              size="icon-sm"
              variant="ghost"
              onClick={(e) => handleEditNote(e, note.id)}
              title="Edit"
            >
              <i className="fa-solid fa-edit" />
            </IconButton>
            <IconButton
              size="icon-sm"
              variant="ghost"
              onClick={(e) => handleDeleteNote(e, note.id)}
              title="Delete"
              className="delete-button"
            >
              <i className="fa-solid fa-trash" />
            </IconButton>
          </div>
        </div>
        
        {/* Content */}
        <div className="note-content">
          <p className="note-summary">
            {truncateContent(note.summary || 'No content available')}
          </p>
        </div>
        
        {/* Footer */}
        <div className="note-footer">
          <div className="note-date">
            <i className="fa-solid fa-clock" />
            <span>{formatDate(note.updatedAt)}</span>
          </div>
          {note.tags && note.tags.length > 0 && (
            <div className="note-tags">
              {note.tags.slice(0, 2).map((tag, index) => (
                <span key={index} className="note-tag">
                  <i className="fa-solid fa-tag" />
                  {tag}
                </span>
              ))}
              {note.tags.length > 2 && (
                <span className="note-tag">+{note.tags.length - 2}</span>
              )}
            </div>
          )}
        </div>
        
        {/* Task connection indicator */}
        {note.taskId ? (
          <div 
            className="task-connection connected"
            onClick={(e) => handleTaskClick(e, note.taskId)}
          >
            <i className="fa-solid fa-tasks" />
            <span>Connected to task</span>
            <div className="task-priority-dot medium" />
          </div>
        ) : (
          <div 
            className="task-connection empty"
            onClick={(e) => handleLinkToTask(e, note)}
          >
            <i className="fa-solid fa-link" />
            <span>Connect to a task</span>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="notes-empty-state">
          <Body color="secondary">Loading notes...</Body>
        </div>
      );
    }
    
    if (filteredNotes.length === 0) {
      return (
        <div className="notes-empty-state">
          <Heading2>No notes found</Heading2>
          <Body color="secondary">Try changing your search or create a new note.</Body>
        </div>
      );
    }
    
    return (
      <div className="notes-grid">
        {filteredNotes.map(renderNoteCard)}
      </div>
    );
  };

  return (
    <CalendarLayout 
      user={user} 
      activeItem="notes" 
      navigationItems={navigationItems} 
      onNavigate={handleNavigation}
      sidebarCollapsed={false}
      className="notes-screen theme-transition"
    >
      <CalendarContainer className="theme-transition">
        <CalendarHeader 
          title="My Notes" 
          onBack={() => navigate('/')} 
          backButton={true}
          className="theme-transition"
          actions={
            <div className="notes-header-actions theme-transition">
              <Button
                variant={darkMode ? "cyan" : "secondary"}
                onClick={handleAddSketch}
                leftIcon={<i className="fa-solid fa-pencil-alt" />}
              >
                Add Sketch
              </Button>
              <Button
                variant={darkMode ? "accent-gradient" : "primary"}
                onClick={handleAddNote}
                leftIcon={<i className="fa-solid fa-plus" />}
                className={darkMode ? "shadow-glow" : ""}
              >
                Add Note
              </Button>
              <Button
                variant={darkMode ? "ghost-accent" : "ghost"}
                onClick={toggleDarkMode}
                leftIcon={<i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />}
                className={darkMode ? "shadow-glow" : ""}
              >
                {darkMode ? 'Light' : 'Dark'}
              </Button>
            </div>
          } 
        />
        
        <div className="notes-container theme-transition">
          {/* Stats Cards */}
          {renderStatsCards()}
          
          {/* Filters */}
          {renderFilterButtons()}
          
          {/* Search */}
          <div className="notes-search theme-transition">
            <SearchInput
              placeholder="Search notes..."
              value={filters.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value })}
              variant={darkMode ? "dark-accent" : "default"}
            />
          </div>
          
          {/* Content */}
          {renderContent()}
        </div>
      </CalendarContainer>

      {/* Task Modal */}
      <SimpleModal
        isOpen={isTaskModalOpen}
        onClose={closeTaskModal}
        title="Connect Note to Task"
        className="task-modal"
      >
        <div className="task-modal-content">
          <div className="task-search">
            <Input
              placeholder="Search tasks..."
              value={taskSearchQuery}
              onChange={(e) => setTaskSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTaskSearch()}
              variant={darkMode ? "dark-accent" : "default"}
              rightIcon={
                <IconButton
                  size="icon-sm"
                  variant={darkMode ? "accent" : "primary"}
                  onClick={handleTaskSearch}
                >
                  <i className="fa-solid fa-search" />
                </IconButton>
              }
            />
          </div>
          
          <div className="task-list">
            {isSearching ? (
              <div className="task-item">Searching...</div>
            ) : searchResults?.tasks?.length ? (
              searchResults.tasks.map(task => (
                <div
                  key={task.id}
                  className={cn("task-item", {
                    selected: selectedTask && task.id === selectedTask.id
                  })}
                  onClick={() => handleTaskSelect(task)}
                >
                  <div className="task-title">{task.title}</div>
                  <Small color="secondary">{task.description}</Small>
                </div>
              ))
            ) : taskSearchQuery ? (
              <div className="task-item">No results found. Try a different search term.</div>
            ) : (
              <div className="task-item">Search for tasks above.</div>
            )}
          </div>
          
          <div className="modal-actions">
            <Button variant="ghost" onClick={closeTaskModal}>
              Cancel
            </Button>
            <Button 
              variant={darkMode ? "accent-gradient" : "primary"}
              onClick={handleConnectTask}
              disabled={!selectedTask}
            >
              Connect
            </Button>
          </div>
        </div>
      </SimpleModal>

      {/* Category Modal */}
      <SimpleModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        title="Create New Category"
        className="category-modal"
      >
        <div className="category-modal-content">
          <Input
            label="Category Name"
            placeholder="Enter category name"
            value={newCategory.name}
            onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
            variant={darkMode ? "dark-accent" : "default"}
          />
          
          <div className="color-selection">
            <Label>Category Color</Label>
            <div className="color-picker-container">
              <div 
                className="color-preview"
                style={{ backgroundColor: newCategory.color }}
              />
              <input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                className="color-picker"
              />
            </div>
            
            <div className="colors-grid">
              {pastelColors.map(color => (
                <button
                  key={color.value}
                  className="color-button"
                  style={{ backgroundColor: color.value }}
                  onClick={() => handleColorSelect(color.value)}
                  title={color.name}
                />
              ))}
            </div>
          </div>
          
          <div className="modal-actions">
            <Button variant="ghost" onClick={handleCloseCategoryModal}>
              Cancel
            </Button>
            <Button 
              variant={darkMode ? "accent-gradient" : "primary"}
              onClick={handleCreateCategory}
              disabled={isCreatingCategory}
            >
              {isCreatingCategory ? 'Creating...' : 'Create Category'}
            </Button>
          </div>
        </div>
      </SimpleModal>
      
      {/* Sketch Editor Modal */}
      <SketchEditor
        isOpen={isSketchModalOpen}
        onClose={handleSketchSave}
        noteId={null}
        initialSketchId={currentSketchId}
      />
    </CalendarLayout>
  );
};

export default NotesScreen; 